<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import KButton from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import { isProdMode } from "@/components/DebugLog/helpers";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { useStore } from "@/store";
    import { Sambi } from "@/store/modules/authModule";
    import { openDialog } from "@/utils";
    import moment from "moment/moment";
    import emitter from "tiny-emitter/instance";
    import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            autorun?: boolean;
            showButton?: boolean;
        }>(),
        {
            autorun: true,
            showButton: true,
        }
    );

    const show = ref(false);
    const url = ref<string | undefined>();
    const sessionId = ref<string | null>(null);

    let timeoutId: number | undefined;

    const sambiData = computed(() => store.getters["auth/sambi"]);

    const toggleShow = () => {
        show.value = !show.value;
    };

    const onSambiClick = async () => {
        await startLogin();
    };

    const showError = () => {
        openDialog({
            description: "Inloggningen i Sambi misslyckades",
            lines: [
                "Kontrollera att du har slutfört inloggningen.",
                "Om problemet kvarstår:",
                "– Be din lokala administratör se över inställningarna",
                "– Kontakta Kliniken-supporten",
            ],
            type: "error",
        });
    };

    const initSambi = async () => {
        try {
            const { data } = await klinikenApi.post<{ redirectUrl: string; sessionId: string }>(
                "nll/sambi/init",
                undefined,
                getConfig({})
            );

            url.value = data.redirectUrl;
            sessionId.value = data.sessionId;
        } catch (error) {
            console.error(error);

            throw error;
        }
    };

    const startLogin = async () => {
        try {
            toggleShow();
            pollSambi();
            window.open(url.value, "_blank")?.focus();
        } catch (error) {
            showError();
        }
    };

    const onCancel = () => {
        clearTimeout(timeoutId);
        toggleShow();
    };

    const pollSambi = async () => {
        try {
            const { data, status } = await klinikenApi.post<Sambi>(
                "nll/sambi/poll",
                { sessionId: sessionId.value },
                getConfig({})
            );

            if (status !== 202) {
                const payloadData = isProdMode({ customHash: "sambi-refresh" })
                    ? data
                    : {
                          ...data,
                          refresh_token: {
                              ...data.refresh_token,
                              validity_end: moment().add(1, "minutes").format("yyyy-MM-DD HH:mm:ss"),
                          },
                      };

                await store.dispatch("auth/addSambi", {
                    data: payloadData,
                });

                emitter.emit("refreshReceptforskrivningar");

                setTimeout(() => {
                    onCancel();
                }, 1000);

                return;
            }

            timeoutId = setTimeout(() => {
                pollSambi();
            }, 3000) as unknown as number;
        } catch (error) {
            console.error(error);
            showError();
        }
    };

    onBeforeMount(async () => {
        if (props.showButton || props.autorun) {
            await initSambi();
        }
    });

    onBeforeUnmount(() => {
        clearTimeout(timeoutId);
    });

    watch(sessionId, async (newValue) => {
        if (newValue && props.autorun) {
            await nextTick();

            startLogin();
        }
    });
</script>

<template>
    <!-- Wrapper för att Vue 2 inte tillåter fler top-level elements. Visa bara sitt innehåll pga display: contents!-->
    <div style="display: contents">
        <KButton v-if="showButton" :href="url" @click="onSambiClick" type="button" class="sambi-button" size="secondary"
            >Logga in i Sambi</KButton
        >
        <portal v-if="show" to="modal">
            <ModalContent v-if="show" showCloseButton title="Inloggning Sambi" @close="onCancel">
                <template #default>
                    <template v-if="sambiData">
                        <p>Du är inloggad!</p>
                    </template>
                    <template v-else>
                        <div class="content">
                            <div>
                                <p>Inloggningen i Sambi startar automatiskt i ett nytt fönster.</p>
                            </div>
                            <p>Väntar på Sambi...</p>
                        </div>
                    </template>
                    <ButtonGroup>
                        <template v-if="!sambiData">
                            <KButton type="button" size="secondary" variant="contour" @click="onCancel">Avbryt</KButton>
                            <KButton type="button" size="secondary" :href="url">Starta manuellt</KButton>
                        </template>
                    </ButtonGroup>
                </template>
            </ModalContent>
        </portal>
    </div>
</template>

<style scoped lang="scss">
    .content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .sambi-button {
        width: fit-content;
    }
</style>
